import { checkFilename } from './filename-check';
import type { FinalLock, ManualChecks, Program, Take, Verdict } from './model';

export interface CheckResult {
  id: string;
  label: string;
  verdict: Verdict;
  detail: string;
}

const verdictFromBoolean = (ok: boolean): Verdict => (ok ? 'pass' : 'fail');

export function evaluateTake(program: Program, take: Take, lock?: FinalLock, manual?: ManualChecks): CheckResult[] {
  const extension = take.fileName.toLowerCase().split('.').pop() ?? '';
  const containerAllowed = program.allowedContainers === null
    ? null
    : program.allowedContainers.some((allowed) => allowed === take.mime || allowed.toLowerCase() === extension);
  const durationKnown = typeof take.durationSeconds === 'number' && Number.isFinite(take.durationSeconds);
  const resolutionKnown = typeof take.width === 'number' && typeof take.height === 'number';
  const resolutionLimited = program.maxWidth != null && program.maxHeight != null;
  const rulesConfirmed = program.rules.every((rule) => rule.state !== 'pending');
  const filename = checkFilename(take.fileName,take.probe,program.fileNamePattern==='ascii');
  const probe=take.probe;
  const codecAliases:Record<string,string>={avc1:'h264',avc3:'h264',hvc1:'hevc',hev1:'hevc',apcn:'prores',apch:'prores',ap4h:'prores'};
  const codec=probe?.videoCodec?codecAliases[probe.videoCodec]??probe.videoCodec:undefined;
  const codecAllowed=codec&&program.allowedCodecs?.some(c=>c.toLowerCase()===codec||(c.toLowerCase()==='h265'&&codec==='hevc'));
  const orientation=take.width&&take.height?(take.width>=take.height?'landscape':'portrait'):undefined;
  const gcd=(a:number,b:number):number=>b?gcd(b,a%b):a;
  const divisor=take.width&&take.height?gcd(take.width,take.height):undefined;
  const ratio=divisor?`${take.width!/divisor}:${take.height!/divisor}`:'';
  const manualCount = manual ? Object.values(manual).filter(Boolean).length : 0;

  return [
    {
      id: 'source', label: '공식 요강 확인 시점', verdict: program.sourceConfirmedAt ? 'pass' : 'check',
      detail: program.sourceConfirmedAt ? `${program.sourceConfirmedAt.slice(0, 10)}에 직접 확인` : '공식 페이지를 열어 현재 규칙인지 직접 확인하세요.',
    },
    {
      id: 'rules', label: '제출 규칙', verdict: rulesConfirmed ? 'pass' : 'check',
      detail: rulesConfirmed ? '모든 규칙에 사용자 판정이 있어요.' : '아직 확인하지 않은 규칙이 있어요.',
    },
    {
      id: 'container', label: '파일 형식', verdict: containerAllowed === null ? 'check' : verdictFromBoolean(containerAllowed),
      detail: containerAllowed === null
        ? `${take.mime || '형식 미상'} · ${take.fileName} — 허용 형식을 요강에서 확인해 넣어 주세요.`
        : `${take.mime || '형식 미상'} · ${take.fileName} / 허용 ${program.allowedContainers?.join(', ')}`,
    },
    {
      id: 'size', label: '파일 크기', verdict: program.maxBytes === null ? 'check' : verdictFromBoolean(take.bytes <= program.maxBytes),
      detail: program.maxBytes === null
        ? `${formatBytes(take.bytes)} — 용량 제한을 요강에서 확인해 넣어 주세요.`
        : `${formatBytes(take.bytes)} / 제한 ${formatBytes(program.maxBytes)}`,
    },
    {
      id: 'duration', label: '재생 길이', verdict: durationKnown && program.maxDurationSeconds !== null
        ? verdictFromBoolean((take.durationSeconds ?? Infinity) <= program.maxDurationSeconds)
        : 'check',
      detail: !durationKnown
        ? '브라우저가 길이를 읽지 못했어요. 직접 재생해 확인하세요.'
        : program.maxDurationSeconds === null
          ? `${formatDuration(take.durationSeconds ?? 0)} — 길이 제한을 요강에서 확인해 넣어 주세요.`
          : `${formatDuration(take.durationSeconds ?? 0)} / 제한 ${formatDuration(program.maxDurationSeconds)}`,
    },
    {
      id: 'resolution', label: '화면 크기', verdict: resolutionKnown && resolutionLimited
        ? verdictFromBoolean((take.width ?? Infinity) <= (program.maxWidth ?? 0) && (take.height ?? Infinity) <= (program.maxHeight ?? 0))
        : 'check',
      detail: !resolutionKnown
        ? '브라우저가 화면 크기를 읽지 못했어요. 파일 정보에서 직접 확인하세요.'
        : !resolutionLimited
          ? `${take.width} × ${take.height} — 화면 크기 제한을 요강에서 확인하세요.`
          : `${take.width} × ${take.height} / 제한 ${program.maxWidth} × ${program.maxHeight}`,
    },
    {
      id: 'codec', label: '영상·소리 재생 방식', verdict: !codec || probe?.error || !program.allowedCodecs ? 'check' : codecAllowed ? 'pass' : 'fail',
      detail: probe?.error ?? (codec ? `${probe?.container?.toUpperCase()??'컨테이너 미상'} · ${codec} · 평균 ${probe?.bitrate?(probe.bitrate/1e6).toFixed(2)+'Mbps':'비트레이트 확인 필요'} · 소리 ${probe?.hasAudio===true?(probe.audioCodec??'있음'):probe?.hasAudio===false?'없음':'확인 필요'} · ${probe?.frameRate?.toFixed(1)??'?'} fps · 지원처 미리보기에서도 확인하세요.` : '내부 정보를 읽지 못했어요. 지원처 미리보기에서 확인하세요.'),
    },
    {id:'filename',label:'파일명',verdict:filename.verdict,detail:filename.issues.join(' · ')||'파일명에서 알려진 오류를 찾지 못했어요.'},
    {id:'orientation',label:'화면 방향',verdict: !orientation||!program.orientation?'check':orientation===program.orientation?'pass':'fail',detail:!orientation?'화면 크기 확인 필요':`${orientation==='landscape'?'가로':'세로'} 영상 · ${ratio} 비율이에요. ${program.orientation?'요강의 방향과 대조했어요.':'요강에 방향 규칙이 있는지 확인하세요.'}`},
    {
      id: 'lock', label: '보낼 영상 선택', verdict: Boolean(take.hash) && lock?.takeId === take.id && lock.hash === take.hash ? 'pass' : 'check',
      detail: Boolean(take.hash) && lock?.takeId === take.id && lock.hash === take.hash ? `파일 확인 코드 ${take.hash.slice(0, 12)}… 일치` : '이 파일을 보낼 영상으로 정하지 않았어요.',
    },
    {
      id: 'manual', label: '직접 재생 확인', verdict: manualCount === 4 ? 'pass' : 'check',
      detail: `${manualCount}/4 완료 · 전체 재생, 소리 맞춤, 시작·끝, 지원처 미리보기`,
    },
  ];
}

export function overallVerdict(results: CheckResult[]): Verdict {
  if (results.some((item) => item.verdict === 'fail')) return 'fail';
  if (results.some((item) => item.verdict === 'check')) return 'check';
  return 'pass';
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(2)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${Math.max(0, Math.round(bytes / 1024))} KB`;
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${rest}`;
}
